import { Controller } from "@hotwired/stimulus";
import { BranchSdk, BranchSdkAdapter, BranchSdkContextError } from "./BranchSdkAdapter";
import { BranchResolveRequestError, resolveBranchRequest } from "./BranchResolveClient";
import { BranchContinuationGuard } from "./BranchContinuationGuard";
import { branchNavigation } from "./BranchNavigation";
import { BranchDecision } from "../Shared/Generated/BranchDecision";
import { BranchBrowserSupport } from "../Shared/Generated/BranchBrowserSupport";
import { BranchReason } from "../Shared/Generated/BranchReason";
import { BranchRecoveryRequest } from "../Shared/Generated/BranchRecoveryRequest";
import { BranchRecoveryCode } from "../Shared/Generated/BranchRecoveryCode";
import { BranchFailurePhase } from "../Shared/Generated/BranchFailurePhase";
import { BranchGuardOutcome } from "../Shared/Generated/BranchGuardOutcome";
import { BranchOutcome } from "../Shared/Generated/BranchOutcome";
import { clearPendingBranchOutcome, rememberBranchArrival, reportBranchOutcome } from "./BranchOutcomeTracking";
import { boundedDuration, captureBrowserDiagnostics, captureMatchContext, clientErrorType, newBranchDiagnostics } from "./BranchDiagnostics";

export default class BranchResolverController extends Controller<HTMLElement> {
    static targets = ["status", "loadingText", "recoveryText", "retry"];
    static values = { key: String, enabled: Boolean };
    declare readonly statusTarget: HTMLElement;
    declare readonly loadingTextTarget: HTMLElement;
    declare readonly recoveryTextTarget: HTMLElement;
    declare readonly retryTarget: HTMLButtonElement;
    declare readonly keyValue: string;
    declare readonly enabledValue: boolean;
    private started = false;
    private navigated = false;
    private retries = 0;
    private attempt?: AbortController;
    private timer?: ReturnType<typeof setTimeout>;
    private attemptId = "";
    private recoveryReported = false;
    private sourceLink: string | null = null;
    private correlationId: string | null = null;
    private diagnostics = newBranchDiagnostics();
    private startedAt = performance.now();
    private readonly pageHide = () => this.cancel();
    private readonly visibilityChange = () => { if (document.visibilityState === "hidden") this.diagnostics.wasHidden = true; };
    private readonly pageShow = (event: PageTransitionEvent) => { if (event.persisted) this.restore(); };

    connect(): void {
        window.addEventListener("pagehide", this.pageHide);
        window.addEventListener("pageshow", this.pageShow);
        document.addEventListener("visibilitychange", this.visibilityChange);
        if (this.started) { this.restore(); return; }
        if (!this.enabledValue) return;
        this.started = true;
        void this.resolve();
    }
    disconnect(): void {
        this.cancel();
        window.removeEventListener("pagehide", this.pageHide);
        window.removeEventListener("pageshow", this.pageShow);
        document.removeEventListener("visibilitychange", this.visibilityChange);
    }
    retry(): void {
        if (this.navigated || this.retries >= 1 || this.retryTarget.hidden) return;
        this.retries++;
        void this.resolve();
    }
    private cancel(reason?: string): void {
        this.attempt?.abort(reason);
        clearTimeout(this.timer);
    }
    private recover(reason: string): void {
        this.cancel(reason);
        this.statusTarget.hidden = false;
        this.statusTarget.textContent = this.recoveryTextTarget.textContent;
        this.retryTarget.hidden = this.retries >= 1 || this.navigated;
        this.reportRecovery(reason);
    }
    private restore(): void {
        this.diagnostics.phase = BranchFailurePhase.HistoryRestoration;
        this.recover("HistoryRestored");
    }
    private newAttemptId(): string {
        return Array.from(crypto.getRandomValues(new Uint8Array(16)), value => value.toString(16).padStart(2, "0")).join("");
    }
    private reportRecovery(reason: string): void {
        if (this.recoveryReported) return;
        this.recoveryReported = true;
        const csrf = this.element.querySelector<HTMLInputElement>('input[name="__RequestVerificationToken"]')?.value;
        if (!csrf) return;
        this.attemptId ||= this.newAttemptId();
        this.diagnostics.elapsedMs = boundedDuration(performance.now() - this.startedAt);
        try { captureBrowserDiagnostics(this.diagnostics, this.startedAt); } catch { /* Optional browser diagnostics must not affect recovery. */ }
        const report: BranchRecoveryRequest = { attemptId: this.attemptId, link: this.sourceLink, reason, correlationId: this.correlationId, diagnostics: { ...this.diagnostics } };
        void fetch("/branch/recovery", {
            method: "POST", credentials: "same-origin", keepalive: true,
            headers: { "Content-Type": "application/json", RequestVerificationToken: csrf },
            body: JSON.stringify(report)
        }).catch(() => { /* Reporting must never interrupt recovery or trigger another report. */ });
    }
    private navigate(url: string, handoff = false): void {
        if (this.navigated) return;
        this.navigated = true;
        this.cancel();
        this.retryTarget.hidden = true;
        this.diagnostics.phase = BranchFailurePhase.Navigation;
        this.diagnostics.elapsedMs = boundedDuration(performance.now() - this.startedAt);
        try {
            if (!handoff && this.sourceLink) rememberBranchArrival({ attemptId: this.attemptId, link: this.sourceLink,
                correlationId: this.correlationId, path: url, createdAt: Date.now(), requestDiagnostics: this.diagnostics });
            branchNavigation.replace(url);
            // Restoring this document after navigation must not overwrite the observed outcome.
            this.recoveryReported = true;
            if (handoff && this.sourceLink) {
                this.diagnostics.elapsedMs = boundedDuration(performance.now() - this.startedAt);
                try { captureBrowserDiagnostics(this.diagnostics, this.startedAt); } catch { /* Optional diagnostics. */ }
                void reportBranchOutcome({ attemptId: this.attemptId, link: this.sourceLink, outcome: BranchOutcome.HandedOff,
                    correlationId: this.correlationId, reason: null, diagnostics: { ...this.diagnostics } },
                    this.element.querySelector<HTMLInputElement>('input[name="__RequestVerificationToken"]')?.value);
            }
        }
        catch (error) { clearPendingBranchOutcome(); this.recoveryReported = false; this.diagnostics.clientErrorType = clientErrorType(error); this.recover(BranchRecoveryCode.NavigationFailed); }
    }
    private async resolve(): Promise<void> {
        this.cancel();
        clearPendingBranchOutcome();
        const attempt = this.attempt = new AbortController();
        this.attemptId = this.newAttemptId();
        this.startedAt = performance.now();
        this.diagnostics = newBranchDiagnostics();
        this.diagnostics.retryNumber = this.retries;
        captureMatchContext(this.diagnostics);
        this.recoveryReported = false;
        this.sourceLink = this.correlationId = null;
        this.retryTarget.hidden = true;
        this.statusTarget.hidden = false;
        this.statusTarget.textContent = this.loadingTextTarget.textContent;
        this.timer = setTimeout(() => this.recover("Deadline"), 15000);
        let failureReason: BranchRecoveryCode = BranchRecoveryCode.SdkUnavailable;
        try {
            failureReason = BranchRecoveryCode.BrowserSupportCheckUnavailable;
            const support = typeof smartGuideBrowserSupport === "undefined" ? "probe-unavailable" : smartGuideBrowserSupport.getStatus();
            const browserSupport = support === "supported" ? BranchBrowserSupport.Supported :
                support === "webgl2-unavailable" ? BranchBrowserSupport.WebGl2Unavailable :
                support === "turbo-syntax-unsupported" ? BranchBrowserSupport.TurboSyntaxUnsupported : null;
            if (browserSupport === null) { this.recover(failureReason); return; }
            failureReason = BranchRecoveryCode.SdkUnavailable;
            const sdk = (window as Window & { branch?: BranchSdk }).branch;
            this.diagnostics.sdkAvailable = !!sdk;
            if (!sdk) throw new Error("SDK unavailable");
            failureReason = BranchRecoveryCode.UnexpectedClientError;
            const request = await new BranchSdkAdapter(sdk, this.keyValue, this.diagnostics).resolveCurrentClick(attempt.signal);
            if (attempt.signal.aborted) return;
            const source = new URL(request.referringUrl);
            if (source.protocol === "https:" && !source.username && !source.password && source.pathname.length <= 256 &&
                /^\/[a-zA-Z0-9_-]+(?:\/[a-zA-Z0-9_-]+)*$/.test(source.pathname)) this.sourceLink = source.origin + source.pathname;
            failureReason = BranchRecoveryCode.ResolveNetworkError;
            this.diagnostics.phase = BranchFailurePhase.ResolveRequest;
            const csrf = this.element.querySelector<HTMLInputElement>('input[name="__RequestVerificationToken"]')?.value;
            if (!csrf || attempt.signal.aborted) throw new Error("Request unavailable");
            const result = await resolveBranchRequest({ ...request, browserSupport }, csrf, this.attemptId, attempt.signal, this.diagnostics, this.startedAt);
            if (attempt.signal.aborted || this.navigated) return;
            failureReason = BranchRecoveryCode.ResolveResponseInvalid;
            if (!result || typeof result !== "object" || Array.isArray(result)) throw new Error("Invalid response");
            this.correlationId = typeof result.correlationId === "string" && /^[a-fA-F0-9]{32}$/.test(result.correlationId) ? result.correlationId : null;
            if (result.decision === BranchDecision.OpenPublicContent && browserSupport !== BranchBrowserSupport.Supported) {
                this.recover(BranchRecoveryCode.UnsupportedBrowserPublicResponse);
                return;
            }
            failureReason = BranchRecoveryCode.UnsafePublicUrl;
            if (result.decision === BranchDecision.OpenPublicContent && typeof result.url === "string" &&
                result.url.startsWith("/destinations/") && !/[?#\\\u0000-\u0020]/.test(result.url)) {
                this.navigate(result.url);
                return;
            }
            if (result.decision === BranchDecision.OpenPublicContent) throw new Error("Unsafe public URL");
            if (result.decision === BranchDecision.ContinueBranch && typeof result.url === "string") {
                this.diagnostics.phase = BranchFailurePhase.ContinuationValidation;
                failureReason = BranchRecoveryCode.ContinuationUrlInvalid;
                const target = new URL(result.url), source = new URL(request.referringUrl);
                const flags = Array.from(target.searchParams).filter(([name]) => name.toLowerCase() === "$do_not_process");
                const unsafe = target.origin !== source.origin ? BranchRecoveryCode.ContinuationOriginMismatch :
                    target.pathname !== source.pathname ? BranchRecoveryCode.ContinuationPathMismatch :
                    target.username || target.password ? BranchRecoveryCode.ContinuationCredentials : target.hash ? BranchRecoveryCode.ContinuationFragment :
                    flags.length !== 1 || flags[0][1] !== "true" ? BranchRecoveryCode.ContinuationFlagInvalid : null;
                if (unsafe) { failureReason = unsafe; throw new Error(unsafe); }
                let storage: Storage | null = null;
                try { storage = sessionStorage; } catch { /* Optional diagnostics. */ }
                this.diagnostics.storageAvailable = !!storage;
                this.diagnostics.phase = BranchFailurePhase.ContinuationGuard;
                const disposition = new BranchContinuationGuard().prepare(request.referringUrl, location.href);
                this.diagnostics.guardOutcome = disposition === "automatic" ? BranchGuardOutcome.Automatic : BranchGuardOutcome.Blocked;
                if (disposition === "automatic") { this.navigate(result.url, true); return; }
                this.recover("LoopBlocked");
                return;
            }
            const serverReason = typeof result.reason === "number" && Object.values(BranchReason).includes(result.reason) ? BranchReason[result.reason] : null;
            this.recover(result.decision === BranchDecision.Recover ? serverReason || BranchRecoveryCode.ResolveResponseInvalid :
                result.decision === BranchDecision.ContinueBranch ? BranchRecoveryCode.ContinuationUrlInvalid : BranchRecoveryCode.UnknownDecision);
        } catch (error) {
            if (!attempt.signal.aborted && !this.navigated) {
                if (!(error instanceof BranchSdkContextError)) this.diagnostics.clientErrorType = error instanceof BranchResolveRequestError ? error.errorType : clientErrorType(error);
                this.recover(error instanceof BranchSdkContextError || error instanceof BranchResolveRequestError ? error.reason : failureReason);
            }
        }
    }
}

